/*
 * QuickAdd.jsx
 * ------------
 * Floating action button (FAB) fixed to the bottom right on mobile.
 * Tapping opens a minimal input sheet from the bottom of the screen.
 * Allows rapid creation of a new checklist without navigating away.
 * Desktop hidden -- desktop uses the inline button on the Checklists page.
 * Used on Checklists and Dashboard pages.
 */

import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import CategoryTag from "./CategoryTag";

// ============================================
// CATEGORY OPTIONS
// ============================================
const CATEGORIES = ["Personal", "Work", "Home", "Shopping"];

// ============================================
// BOTTOM SHEET
// Slides up from bottom on mobile
// ============================================
function BottomSheet({ onClose, onCreate }) {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Personal");
  const [creating, setCreating] = useState(false);
  const [visible, setVisible] = useState(false);
  const inputRef = useRef(null);

  // Animate in
  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  function handleClose() {
    setVisible(false);
    setTimeout(onClose, 300);
  }

  async function handleCreate() {
    if (!title.trim()) return;
    setCreating(true);
    try {
      await onCreate({ title: title.trim(), category });
      handleClose();
    } catch (err) {
      console.error("QuickAdd create error:", err);
    } finally {
      setCreating(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter") handleCreate();
    if (e.key === "Escape") handleClose();
  }

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={handleClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.4)",
          zIndex: 1300,
          opacity: visible ? 1 : 0,
          transition: "opacity 300ms ease"
        }}
      />

      {/* Sheet */}
      <div style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        background: "var(--bg-surface)",
        borderRadius: "var(--radius-lg) var(--radius-lg) 0 0",
        padding: "var(--space-2) var(--space-5) var(--space-7)",
        zIndex: 1301,
        boxShadow: "var(--shadow-lg)",
        transform: visible ? "translateY(0)" : "translateY(100%)",
        transition: "transform 300ms cubic-bezier(0.4, 0, 0.2, 1)"
      }}>

        {/* Drag handle indicator */}
        <div style={{
          width: "36px",
          height: "4px",
          borderRadius: "2px",
          background: "var(--border-strong)",
          margin: "var(--space-2) auto var(--space-5)"
        }} />

        {/* Title */}
        <h3 style={{
          fontFamily: "var(--font-display)",
          fontWeight: 700,
          fontSize: "20px",
          color: "var(--text-primary)",
          margin: "0 0 var(--space-5)"
        }}>
          New Checklist
        </h3>

        {/* Checklist name input */}
        <input
          ref={inputRef}
          type="text"
          value={title}
          onChange={e => setTitle(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="What's this checklist for?"
          maxLength={80}
          style={{
            width: "100%",
            fontFamily: "var(--font-body)",
            fontSize: "16px",
            color: "var(--text-primary)",
            background: "var(--bg-surface-2)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-md)",
            padding: "var(--space-4)",
            outline: "none",
            marginBottom: "var(--space-4)",
            transition: "border-color var(--transition-fast)",
            boxSizing: "border-box"
          }}
          onFocus={e => e.target.style.borderColor = "var(--accent-green)"}
          onBlur={e => e.target.style.borderColor = "var(--border)"}
        />

        {/* Category selector */}
        <div style={{
          display: "flex",
          gap: "var(--space-2)",
          flexWrap: "wrap",
          marginBottom: "var(--space-5)"
        }}>
          {CATEGORIES.map(cat => (
            <CategoryTag
              key={cat}
              category={cat}
              size="md"
              active={category === cat}
              onClick={() => setCategory(cat)}
            />
          ))}
        </div>

        {/* Create button */}
        <button
          onClick={handleCreate}
          disabled={!title.trim() || creating}
          style={{
            width: "100%",
            background: title.trim() ? "var(--accent-green)" : "var(--bg-surface-2)",
            border: "none",
            borderRadius: "var(--radius-md)",
            padding: "var(--space-4)",
            cursor: title.trim() ? "pointer" : "not-allowed",
            fontFamily: "var(--font-body)",
            fontWeight: 600,
            fontSize: "16px",
            color: title.trim() ? "white" : "var(--text-muted)",
            transition: "background var(--transition-fast), opacity var(--transition-fast)",
            opacity: creating ? 0.7 : 1
          }}
        >
          {creating ? "Creating..." : "Create Checklist"}
        </button>
      </div>
    </>
  );
}

// ============================================
// FAB BUTTON
// Fixed bottom right, hidden on desktop
// ============================================
function FABButton({ onClick }) {
  const [pressed, setPressed] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onTouchStart={() => setPressed(true)}
      onTouchEnd={() => setPressed(false)}
      style={{
        position: "fixed",
        bottom: "calc(64px + var(--space-4))", // above mobile tab bar
        right: "var(--space-5)",
        width: "56px",
        height: "56px",
        borderRadius: "50%",
        background: "var(--accent-green)",
        border: "none",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 4px 16px rgba(45, 106, 79, 0.4)",
        zIndex: 900,
        transform: pressed ? "scale(0.92)" : "scale(1)",
        transition: "transform 150ms ease, box-shadow 150ms ease",
        // Hidden on desktop
        "@media (min-width: 768px)": { display: "none" }
      }}
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path
          d="M12 4V20M4 12H20"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    </button>
  );
}

// ============================================
// MAIN EXPORT
// ============================================
export default function QuickAdd({ onCreate }) {
  const { isGuest, user } = useAuth();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Track window size
  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth < 768);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Only render on mobile
  if (!isMobile) return null;

  async function handleCreate(data) {
    const headers = { "Content-Type": "application/json" };
    if (isGuest) headers["x-guest-id"] = user?.id;

    const res = await fetch("/api/checklists", {
      method: "POST",
      headers,
      body: JSON.stringify({
        title: data.title,
        category: data.category,
        items: [],
        recurring: false
      })
    });

    if (!res.ok) throw new Error("Failed to create checklist");
    const created = await res.json();
    if (onCreate) onCreate(created);
  }

  return (
    <>
      <FABButton onClick={() => setSheetOpen(true)} />
      {sheetOpen && (
        <BottomSheet
          onClose={() => setSheetOpen(false)}
          onCreate={handleCreate}
        />
      )}
    </>
  );
}